import {
  Box,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import ResponsiveIframe from "components/ResponsiveIframe";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useMemo } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  Profession,
  ResponseOfProfession,
  SkuMajorTopic,
} from "services/KnowfullyAdminClient";
import { SkuFormProps } from "types/sku-related-types";
import SkuMajorTopicForm from "./SkuMajorTopicForm";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";

const useStyles = makeStyles((theme: Theme) =>
  ({
    paperWrapper: {
      width: "100%",
      overflow: "auto",
      backgroundColor: theme.palette.primary.light,
    },
    paper: {
      width: "100%",
      height: "20vh",
      overflow: "auto",
      marginTop: "15px",
    },
    professionLabel: {
      paddingTop: "10px",
      fontWeight: "bold",
      color: theme.palette.primary.dark,
      textDecoration: "underline",
    },
    unspecifiedLabel: {
      paddingTop: "10px",
      color: "grey",
    },
    descriptionBox: {
      width: "90%",
    },
    actions: {
      width: "10%",
      display: "inline-block",
      verticalAlign: "top",
      textAlign: "right",
    },
    editButton: {
      "&:hover": {
        color: "orange",
      },
    },
    deleteButton: {
      "&:hover": {
        color: "red",
      },
    },
  })
);

type SkuMajorTopicBoxProps = {
  skuMajorTopic: SkuMajorTopic;
  deleteMajorTopic?: (id: string) => Promise<void> | undefined;
  showActions?: boolean;
  index: number;
};

const SkuMajorTopicBox = ({
  sku,
  skuMajorTopic,
  deleteMajorTopic,
  showActions = true,
  index,
  ...props
}: SkuMajorTopicBoxProps & SkuFormProps) => {
  const styles = useStyles(useTheme());
  const [createOpen, createOpenActions] = useBoolean(false);
  const queryClient = useQueryClient();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { skuService } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const { data, isFetching } = useQuery<ResponseOfProfession>(
    ["profession", skuMajorTopic?.professionId!],
    () => skuService.getProfessionById(skuMajorTopic?.professionId!),
    {
      enabled: skuMajorTopic?.professionId !== null,
    }
  );

  const profession = useMemo(() => (data?.data ?? null) as Profession | null, [
    data?.data,
  ]);

  const deleteSkuMajorTopic = async () => {
    try {
      const response = await deleteMajorTopic!(skuMajorTopic?.id!);
      queryClient.invalidateQueries(["skuMajorTopics", sku.id!]);
      return response;
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Paper sx={styles.paperWrapper}>
        <Grid container>
          <Grid item xs={12}>
            <Box display="flex" mt={1} mb={2} ml={2} mr={1}>
              <Box sx={styles.descriptionBox}>
                {!isFetching ? (
                  /*If profession is null, we show the label, otherwise a fragment */
                  profession !== null ? (
                    <Typography sx={styles.professionLabel}>
                      {profession?.name!}
                    </Typography>
                  ) : (
                    <Tooltip
                      title={t({
                        id: "unspecifiedProfessionMessage",
                      })}
                      style={{ textAlign: "left" }}
                    >
                      <Typography sx={styles.unspecifiedLabel}>
                        <T id="unspecified" />
                      </Typography>
                    </Tooltip>
                  )
                ) : (
                  <CircularProgress size={16} />
                )}
                <Paper
                  sx={styles.paper}
                  key={skuMajorTopic.id}
                  data-cy={`majorTopic${index}Text`}
                >
                  <ResponsiveIframe
                    srcDoc={skuMajorTopic.majorTopic?.description!}
                    title="majorTopicHtml"
                    containerStyles={{ height: "100%" }}
                  />
                </Paper>
              </Box>
              <Box sx={styles.actions}>
                {showActions && (
                  <>
                    <IconButton
                      color="primary"
                      component="span"
                      sx={styles.editButton}
                      onClick={createOpenActions.setTrue}
                      data-cy={`majorTopic${index}EditButton`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      component="span"
                      sx={styles.deleteButton}
                      onClick={() => deleteOpenActions.setTrue()}
                      data-cy={`majorTopic${index}DeleteButton`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <DeleteEntityDialog
        dialogTitleKey="skuMajorTopicDeleteTitle"
        contentKey="skuMajorTopicDeleteQuestion"
        open={deleteOpen}
        handleDelete={deleteSkuMajorTopic}
        onCancel={deleteOpenActions.setFalse}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuMajorTopicForm
          skuMajorTopic={skuMajorTopic}
          onClose={createOpenActions.setFalse}
          sku={sku}
          {...props}
        />
      </Dialog>
    </>
  );
};

export default SkuMajorTopicBox;
